import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
} from "@mui/material";
import { IconHistory, IconUser, IconClock } from "@tabler/icons-react";
import moment from "moment";

const ContractStatusHistory = ({ contractId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/backend/contract-status-history/?contract_id=${contractId}`);
      if (!response.ok) {
        throw new Error("Falha ao carregar histórico de status");
      }
      const data = await response.json();
      setHistory(data.results || []);
    } catch (err) {
      setError("Erro ao carregar histórico de status");
      console.error("Erro ao buscar histórico:", err);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    if (contractId) {
      fetchHistory();
    }
  }, [contractId, fetchHistory]);

  const getStatusColor = (status) => {
    const statusColors = {
      'AJUIZAR': 'primary',
      'NAO_POSSUI_ABUSIVIDADE': 'warning',
      'FALTAM_DOCUMENTOS': 'error',
      'CANCELADO': 'default',
      'PROTOCOLO_URGENTE': 'secondary',
      'PROTOCOLADO': 'info',
      'TRANSITO_EM_JULGADO': 'success',
      'REAJUIZAR': 'primary',
      'PASTA_FEITA': 'info',
      'DESATUALIZADO': 'warning',
    };
    return statusColors[status] || 'default';
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('DD/MM/YYYY HH:mm');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Carregando histórico...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (history.length === 0) {
    return (
      <Box p={3} textAlign="center">
        <IconHistory size={48} color="#ccc" />
        <Typography variant="h6" color="textSecondary" sx={{ mt: 2 }}>
          Nenhum histórico encontrado
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Este contrato ainda não teve alterações de status registradas.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Chip 
          label={`${history.length} registro${history.length !== 1 ? 's' : ''}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Stack>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Data/Hora</TableCell>
              <TableCell>Usuário</TableCell>
              <TableCell>Status Anterior</TableCell>
              <TableCell>Novo Status</TableCell>
              <TableCell>Observações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.map((record) => (
              <TableRow 
                key={record.id}
                hover
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconClock size={16} color="#666" />
                    <Typography variant="body2">
                      {formatDate(record.changed_at)}
                    </Typography>
                  </Stack>
                </TableCell>
                
                <TableCell>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                      <IconUser size={14} />
                    </Avatar>
                    <Typography variant="body2">
                      {record.user_name || 'Usuário'}
                    </Typography>
                  </Stack>
                </TableCell>
                
                <TableCell>
                  {record.previous_status ? (
                    <Chip
                      label={record.previous_status_display}
                      size="small"
                      color={getStatusColor(record.previous_status)}
                      variant="outlined"
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      -
                    </Typography>
                  )}
                </TableCell>
                
                <TableCell>
                  <Chip
                    label={record.new_status_display}
                    size="small"
                    color={getStatusColor(record.new_status)}
                  />
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {record.notes || '-'}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ContractStatusHistory;